import React, { useState, useEffect } from "react";
import { Box, Typography, Link, Card, CardContent } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import CurrentBankCard from "./CurrentBankCard";
import StripeContainer from "./Stripe/StripeContainer";
import { fetchCardDetails, fetchUserBalance, fetchUserSubscriptions } from "../../../services/api";
import colors from "../../../theme";
import Stripe_logo from "../../../assets/Stripe_logo.png";

import AmericanExpress from "../../../assets/CardLogos/AmericanExpress.png";
import Discover from "../../../assets/CardLogos/Discover.png";
import JCB from "../../../assets/CardLogos/JCB.png";
import MasterCard from "../../../assets/CardLogos/MasterCard.png";
import Visa from "../../../assets/CardLogos/Visa.png";
import UnionPay from "../../../assets/CardLogos/UnionPay.png";

import CurrentBalance from "../../../components/Cards/CurrentBalance";
import TopupCard from "../../../components/Cards/TopupCard";
import Loader from "../../../components/Loader/Loader";

const CardDetails = () => {
  const [refresh, setRefresh] = useState(false);
  const [cardDetails, setCardDetails] = useState(null);

  const [currentUserBalance, setCurrentUserBalance] = useState(0);
  const [subscriptions, setSubscriptions] = useState([]);

  const [loading, setLoading] = useState(true);

  const handleRefresh = () => {
    setRefresh((prev) => !prev);
  };

  useEffect(() => {
    getCardDetails();
    getCurrentBalance();
    getSubscriptions();
    const timerId = setTimeout(() => {
      getCurrentBalance();
    }, 9000);

    return () => clearTimeout(timerId);
  }, [refresh]);

  const getCardDetails = async () => {
    try {
      const data = await fetchCardDetails();
      if (data) {
        setCardDetails({
          id: data.id,
          lastFour: `**** **** **** ${data.lastFour}`,
          holderName: data.holderName || "",
          expiryDate: data.expiryDate,
          brand: data?.brand,
        });
      } else {
        setCardDetails(null);
      }
    } catch (error) {
      console.error("Error fetching card details:", error);
      setCardDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentBalance = async () => {
    try {
      const data = await fetchUserBalance();
      if (data) {
        setCurrentUserBalance(data);
      }
    } catch (error) {
      console.error("Error fetching card details:", error);
    }
  };

  const getSubscriptions = async () => {
    try {
      const data = await fetchUserSubscriptions();
      setSubscriptions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      setSubscriptions([]);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Box component="main" sx={{ flex: 1, py: 2, px: 4, bgcolor: "#eaeff1" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <Typography>
            Payments are powered by
            <img
              src={Stripe_logo}
              alt={"Stripe_logo"}
              style={{ width: 60, height: 25 }}
            />
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            mb: 2,
          }}
        >
          <img src={Visa} alt="Visa" style={{ width: 30, margin: "0 10px" }} />
          <img
            src={MasterCard}
            alt="Master Card"
            style={{ width: 30, margin: "0 10px" }}
          />
          <img
            src={AmericanExpress}
            alt="American Express"
            style={{ width: 30, margin: "0 10px" }}
          />
          <img
            src={Discover}
            alt="Discover"
            style={{ width: 30, margin: "0 10px" }}
          />
          <img src={JCB} alt="JCB" style={{ width: 30, margin: "0 10px" }} />
          <img
            src={UnionPay}
            alt="Union Pay"
            style={{ width: 30, margin: "0 10px" }}
          />
        </Box>
        <Box>
          {cardDetails ? (
            <CurrentBankCard
              cardDetails={cardDetails}
              onDelete={handleRefresh}
            />
          ) : (
            <StripeContainer onAddCard={handleRefresh} />
          )}
        </Box>
        <Box>
          <CurrentBalance currentUserBalance={currentUserBalance} />
        </Box>

        <Box
          mb={2}
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            gap: 5,
          }}
        >
          <TopupCard value={5.0} text="Topup Threshold" />
          <TopupCard value={20.0} text="Topup Amount" />
        </Box>
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Card sx={{ backgroundColor: colors.mainContentBgColor, width: "100%", maxWidth: 400, textAlign: "center"}}>
            <CardContent>
              <Typography sx={{ fontWeight: 700, mb: 1, color: colors.secondary }}>
                Platform Subscription
              </Typography>
              {subscriptions.length === 0 ? (
                <Typography sx={{ fontSize: 14 }}>
                  No active subscription.{" "}
                  <Link component={RouterLink} to="/tariffs" sx={{ color: colors.secondary, fontWeight: 700, textDecoration: "none", "&:hover": { color: colors.btnBackgroundColorOnHover, textDecoration: "none"}}}>
                    View tariff details
                  </Link>
                </Typography>
              ) : (
                subscriptions.map((item) => (
                  <Box key={item.id} sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontSize: 14 }}>
                      Status: {!item.autoRenewal ? "Cancelled, active until period end" : "Active"}
                    </Typography>
                    <Typography sx={{ fontSize: 14 }}>
                      Renewal: {item.autoRenewal || item.willRenew ? "Will renew" : "Will not renew"}
                    </Typography>
                    <Typography sx={{ fontSize: 14 }}>
                      Expiry: {(item.expiryDate || item.periodEnd)
                        ? new Date(item.expiryDate || item.periodEnd).toLocaleDateString()
                        : "-"}
                      {item.buildingNumber ? ` (${item.buildingNumber})` : ""}
                    </Typography>
                    <Link component={RouterLink} to="/tariffs" sx={{ color: colors.secondary, fontWeight: 700, textDecoration: "none", "&:hover": {  color: colors.btnBackgroundColorOnHover, textDecoration: "none"}}}>
                      View tariff details
                    </Link>
                  </Box>
                ))
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </>
  );
};

export default CardDetails;
